<template>
  <v-container>
    <div class="text-center">
      <SectionTitle class="d-inline-block">Duas formas de ganhar com cada cliente</SectionTitle>
      <p class="section-subtitle mt-4">
        Você ganha no fechamento do contrato e continua ganhando todo mês com a carteira que construiu.
      </p>
    </div>

    <v-row class="mt-8">
      <v-col v-for="(card, i) in cards" :key="i" cols="12" md="6">
        <div class="gain-card">
          <div class="gain-card__top">
            <div class="gain-card__icon">
              <v-icon size="28" color="error">{{ card.icon }}</v-icon>
            </div>
            <span class="gain-card__eyebrow">{{ card.eyebrow }}</span>
          </div>
          <div class="gain-card__value">{{ card.value }}</div>
          <p class="gain-card__desc">{{ card.desc }}</p>
        </div>
      </v-col>
    </v-row>

    <div class="ladder mt-8">
      <div class="ladder__text">
        <h3 class="ladder__title">Comissão recorrente crescente</h3>
        <p class="ladder__desc">
          Quanto mais clientes na sua carteira, maior o percentual que você recebe todo mês.
        </p>
      </div>

      <div class="ladder__steps">
        <div v-for="(step, i) in steps" :key="i" class="ladder__step">
          <div class="ladder__track">
            <div class="ladder__bar" :style="{ '--size': `${step.height}%` }">
              <span class="ladder__pct">{{ step.percentual }}%</span>
            </div>
          </div>
          <span class="ladder__label">{{ step.label }}</span>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
  import SectionTitle from '@/components/SectionTitle.vue';
  import { FAIXAS_RECORRENTE, rotuloFaixa } from '@/utils/comissaoRevenda';

  export default {
    name: 'RevendaGanhosSection',

    components: {
      SectionTitle,
    },

    data: () => ({
      cards: [
        {
          icon: 'mdi-cash-fast',
          eyebrow: 'Na venda',
          value: 'Até 80%',
          desc: 'do valor da primeira mensalidade de cada cliente que você fechar.',
        },
        {
          icon: 'mdi-autorenew',
          eyebrow: 'Todo mês',
          value: '20% a 35%',
          desc: 'das mensalidades da sua carteira, com percentual que cresce conforme ela aumenta.',
        },
      ],
    }),

    computed: {
      // Altura proporcional ao percentual, com a maior faixa ocupando a trilha inteira.
      steps() {
        const maior = FAIXAS_RECORRENTE[FAIXAS_RECORRENTE.length - 1].percentual;

        return FAIXAS_RECORRENTE.map(faixa => ({
          label: rotuloFaixa(faixa),
          percentual: faixa.percentual,
          height: Math.round((faixa.percentual / maior) * 100),
        }));
      },
    },
  };
</script>

<style scoped lang="scss">
  .section-subtitle {
    font-size: 17px;
    line-height: 1.7;
    color: var(--text-muted);
    max-width: 600px;
    margin: 0 auto;

    @media (max-width: 780px) {
      font-size: 15px;
    }
  }

  /* ── Cards ── */
  .gain-card {
    height: 100%;
    padding: 28px 26px;
    border-radius: 18px;
    background: var(--card-bg);
    border: 1px solid var(--card-border);
    transition: transform 0.28s ease, border-color 0.28s ease, box-shadow 0.28s ease;

    &:hover {
      transform: translateY(-6px);
      border-color: rgba(244, 6, 18, 0.45);
      box-shadow: 0 10px 28px rgba(244, 6, 18, 0.1);
    }

    &__top {
      display: flex;
      align-items: center;
      gap: 14px;
    }

    &__icon {
      width: 54px;
      height: 54px;
      border-radius: 14px;
      background: rgba(244, 6, 18, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    &__eyebrow {
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--text-muted);
    }

    &__value {
      margin-top: 22px;
      font-size: 46px;
      font-weight: 800;
      line-height: 1;
      color: #23cf65;

      @media (max-width: 780px) {
        font-size: 38px;
      }
    }

    &__desc {
      margin: 12px 0 0;
      font-size: 16px;
      line-height: 1.6;
      color: var(--text-secondary);
    }
  }

  /* ── Escada da recorrência ── */
  .ladder {
    display: grid;
    grid-template-columns: 1fr 1.3fr;
    gap: 40px;
    align-items: center;
    padding: 32px;
    border-radius: 18px;
    background: var(--card-bg);
    border: 1px solid var(--card-border);
    border-left: 3px solid #23cf65;

    @media (max-width: 960px) {
      grid-template-columns: minmax(0, 1fr);
      gap: 28px;
      padding: 26px 22px;
    }

    &__title {
      font-size: 22px;
      font-weight: 700;
      margin: 0 0 10px;
      color: var(--text-primary);
    }

    &__desc {
      font-size: 15px;
      line-height: 1.7;
      color: var(--text-muted);
      margin: 0;
    }

    &__steps {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 12px;
    }

    &__track {
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      height: 160px;
    }

    &__bar {
      height: var(--size);
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding-top: 8px;
      border-radius: 10px 10px 4px 4px;
      background: linear-gradient(180deg, #23cf65 0%, rgba(35, 207, 101, 0.3) 100%);
    }

    &__pct {
      font-size: 15px;
      font-weight: 800;
      line-height: 1;
      color: #ffffff;
    }

    &__label {
      margin-top: 10px;
      min-height: 34px;
      font-size: 12px;
      font-weight: 600;
      line-height: 1.4;
      text-align: center;
      color: var(--text-secondary);
    }

    /* Celular: 4 colunas não comportam os rótulos; a escada deita e cresce para a direita. */
    @media (max-width: 600px) {
      &__steps {
        grid-template-columns: minmax(0, 1fr);
        gap: 10px;
      }

      &__step {
        display: grid;
        grid-template-columns: 110px minmax(0, 1fr);
        align-items: center;
        gap: 12px;
      }

      &__label {
        order: -1;
        margin: 0;
        min-height: 0;
        text-align: left;
      }

      &__track {
        flex-direction: row;
        justify-content: flex-start;
        height: 30px;
      }

      &__bar {
        width: var(--size);
        height: 100%;
        align-items: center;
        justify-content: flex-end;
        padding: 0 8px 0 0;
        border-radius: 4px 10px 10px 4px;
        background: linear-gradient(90deg, rgba(35, 207, 101, 0.3) 0%, #23cf65 100%);
      }
    }
  }
</style>
